import {
  AlignmentType, Document, HeadingLevel, LineNumberRestartFormat, Packer, Paragraph, TextRun,
} from 'docx';
import { saveAs } from 'file-saver';
import { markdownToDocxParagraphs } from 'shared/utils/richTextMarkdown';
import { applyInTextMap, buildEndNoteMap, toRis } from './citations';

// Journal-submission manuscript export (.docx with continuous line numbers).
//
// mode 'formatted': citations and reference list in the publication's citation style
//                   (styledBody supplies the formatted section text).
// mode 'endnote':   in-text citations become EndNote temporary citations
//                   ({Author, Year}) and the References section is left out; a matching
//                   .ris file is downloaded for import into the EndNote library. In Word,
//                   EndNote's "Update Citations and Bibliography" then links the citations
//                   and builds the reference list in any EndNote output style.

const isReferences = (section) => section.title?.trim().toLowerCase() === 'references';

const fileBase = (pub) => (pub.acronym || pub.title || 'manuscript').replace(/[^\w-]+/g, '_').slice(0, 60);

const titleBlock = (pub) => {
  const authors = pub.authors || [];
  const affiliations = [];
  authors.forEach((a) => {
    const aff = (a.affiliation || '').trim();
    if (aff && !affiliations.includes(aff)) affiliations.push(aff);
  });
  const affIndex = (a) => affiliations.indexOf((a.affiliation || '').trim()) + 1;
  const corresponding = authors.filter((a) => a.is_corresponding && a.email);

  return [
    new Paragraph({ heading: HeadingLevel.TITLE, alignment: AlignmentType.CENTER, children: [new TextRun(pub.title || pub.acronym || '')] }),
    new Paragraph({
      alignment: AlignmentType.CENTER,
      spacing: { after: 120 },
      children: authors.flatMap((a, i) => [
        new TextRun(`${i ? ', ' : ''}${a.name}`),
        ...(affIndex(a) || a.is_corresponding
          ? [new TextRun({ text: [affIndex(a) || null, a.is_corresponding ? '*' : null].filter(Boolean).join(','), superScript: true })]
          : []),
      ]),
    }),
    ...affiliations.map((aff, i) => new Paragraph({
      alignment: AlignmentType.CENTER,
      children: [new TextRun({ text: String(i + 1), superScript: true }), new TextRun({ text: aff, italics: true })],
    })),
    ...corresponding.map((a) => new Paragraph({
      alignment: AlignmentType.CENTER,
      spacing: { after: 240 },
      children: [new TextRun({ text: `*Corresponding author: ${a.name} (${a.email})`, size: 20 })],
    })),
  ];
};

/**
 * pub: the publication (title, authors, abstract, keywords, references, sections).
 * styledBody(section): section markdown with citations in the chosen style.
 */
export const exportManuscript = async ({ pub, mode = 'formatted', styledBody, figureImages = {} }) => {
  const sections = (pub.sections || [])
    .filter((s) => s.section_key !== 'abstract' && s.section_key !== 'appendix')
    .sort((a, b) => a.position - b.position);
  const references = pub.references || [];
  const figures = { n: 0 }; // figure placeholders numbered across the manuscript

  let bodyOf = styledBody;
  if (mode === 'endnote') {
    const endNoteMap = buildEndNoteMap(sections.map((s) => s.current_content?.body || ''), references);
    bodyOf = (section) => applyInTextMap(section.current_content?.body || '', endNoteMap);
  }

  const children = [...titleBlock(pub)];
  if (pub.abstract) {
    children.push(new Paragraph({ heading: HeadingLevel.HEADING_1, children: [new TextRun('Abstract')] }));
    children.push(...markdownToDocxParagraphs(pub.abstract, { figureCounter: figures, figureImages }));
  }
  const keywords = (pub.keywords || '').split(',').map((k) => k.trim()).filter(Boolean);
  if (keywords.length) {
    children.push(new Paragraph({ children: [new TextRun({ text: 'Keywords: ', bold: true }), new TextRun(keywords.join('; '))] }));
  }

  sections.forEach((section) => {
    if (mode === 'endnote' && isReferences(section)) return; // EndNote builds the bibliography
    children.push(new Paragraph({
      heading: HeadingLevel.HEADING_1,
      spacing: { before: 300, after: 120 },
      children: [new TextRun(`${section.section_key}. ${section.title}`)],
    }));
    children.push(...markdownToDocxParagraphs(bodyOf(section), { figureCounter: figures, figureImages }));
  });

  if (mode === 'endnote') {
    // EndNote inserts the formatted bibliography after this marker paragraph
    children.push(new Paragraph({ heading: HeadingLevel.HEADING_1, spacing: { before: 300 }, children: [new TextRun('References')] }));
  }

  const doc = new Document({
    sections: [{
      properties: {
        // Continuous line numbers, as journals require for review
        lineNumbers: { countBy: 1, restart: LineNumberRestartFormat.CONTINUOUS },
      },
      children,
    }],
  });
  const base = fileBase(pub);
  saveAs(await Packer.toBlob(doc), mode === 'endnote' ? `${base}_EndNote.docx` : `${base}_manuscript.docx`);

  if (mode === 'endnote') {
    const cited = references.filter((r) => r.cited);
    const ris = await toRis(cited.length ? cited : references);
    saveAs(new Blob([ris], { type: 'application/x-research-info-systems;charset=utf-8' }), `${base}_references.ris`);
  }
};

/** RIS file of the whole bibliography. */
export const exportRis = async (pub) => {
  const ris = await toRis(pub.references || []);
  saveAs(new Blob([ris], { type: 'application/x-research-info-systems;charset=utf-8' }), `${fileBase(pub)}_references.ris`);
};
